// src/pages/customers/CustomerListPage/CustomerListPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import CustomersAPI, {
  unwrapItems,
  unwrapItem as unwrapCustomer,
} from '../../../api/v3/customers-api-v3';

import ManualDeleteButton, { MANUAL_DELETE_NOTE } from '../../../components/bagspa/ManualDeleteButton';

const BASE = '/app/sales/bagspa';

// -------- helpers --------
function includesCi(haystack = '', needle = '') {
  return (haystack || '').toString().toLowerCase().includes((needle || '').toString().toLowerCase());
}
function toDateMs(v) {
  const t = Date.parse(v);
  return Number.isNaN(t) ? 0 : t;
}
function toNum(v) {
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

// Ensure SheetJS (XLSX) is available at runtime (CDN)
async function ensureXLSX() {
  if (window.XLSX) return window.XLSX;
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.sheetjs.com/xlsx-latest/package/dist/xlsx.full.min.js';
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return window.XLSX;
}

export default function CustomerListPage() {
  // data + ui state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState({ page: 1, per: 25, total: 0, total_pages: 1 });

  // filters / sorting / paging (server-side)
  const [q, setQ] = useState('');
  const [sortBy, setSortBy] = useState({ key: 'id', dir: 'desc' }); // id|name|email|created_at
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(25);

  // export flag
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const json = await CustomersAPI.list({
        q,
        page,
        per,
        sort_by: sortBy.key,
        sort_dir: sortBy.dir,
      });

      const items = unwrapItems(json);
      const nextRows = Array.isArray(items) ? items : [];
      const nextMeta = json?.meta || {};

      setRows(nextRows);
      setMeta({
        page: Number(nextMeta.page || page),
        per: Number(nextMeta.per || per),
        total: Number(nextMeta.total || nextRows.length),
        total_pages: Number(nextMeta.total_pages || Math.max(1, Math.ceil(Number(nextMeta.total || nextRows.length) / Number(nextMeta.per || per || 25)))),
      });
    } catch (e) {
      setError(e.message || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  }, [q, page, per, sortBy]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Number(meta.total_pages || 1));
  const pageSafe = Math.min(page, totalPages);
  const paged = rows;

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  function toggleSort(key) {
    setSortBy((prev) =>
      prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
    );
    setPage(1);
  }

  // -------- Export (all filtered rows, not only current page) --------
  async function fetchAllSeedsForExport() {
    const out = [];
    let currentPage = 1;
    let lastPage = 1;

    do {
      const json = await CustomersAPI.list({
        q,
        page: currentPage,
        per: 200,
        sort_by: sortBy.key,
        sort_dir: sortBy.dir,
      });

      const items = unwrapItems(json);
      const meta = json?.meta || {};

      out.push(
        ...(Array.isArray(items) ? items : []).map((c) => ({
          id: c.id,
          name: c.name,
          email: c.email,
          phone: c.phone,
          created_at: c.created_at,
          updated_at: c.updated_at,
        }))
      );

      lastPage = Number(meta.total_pages || 1);
      currentPage += 1;
    } while (currentPage <= lastPage);

    return out;
  }

  // Hydrate with full addresses (first/default address flattened)
  async function hydrateForExport() {
    const seedsForExport = await fetchAllSeedsForExport();
    const out = [];
    const concurrency = 6;
    let i = 0;

    async function worker() {
      while (i < seedsForExport.length) {
        const idx = i++;
        const seed = seedsForExport[idx];
        try {
          const j = await CustomersAPI.show(seed.id);
          const full = unwrapCustomer(j) || {};
          const addr = Array.isArray(full.addresses) && full.addresses.length ? full.addresses[0] : {};

          out[idx] = {
            id: seed.id,
            name: full.name || seed.name || '',
            email: full.email || seed.email || '',
            phone: full.phone || seed.phone || '',
            // legacy single-line, include if you expose it in serializer
            address_legacy: full.address || '',
            street: addr.street || '',
            subdistrict: addr.subdistrict || '',
            district: addr.district || '',
            city: addr.city || '',
            province: addr.province || '',
            postal_code: addr.postal_code || '',
            latitude: addr.latitude ?? '',
            longitude: addr.longitude ?? '',
            recipient_name: addr.recipient_name || '',
            telephone: addr.telephone || '',
            alias: addr.alias || '',
            created_at: dayjs(full.created_at || seed.created_at).format('YYYY-MM-DD HH:mm:ss'),
            updated_at: dayjs(full.updated_at || seed.updated_at).format('YYYY-MM-DD HH:mm:ss'),
          };
        } catch {
          out[idx] = {
            id: seed.id,
            name: seed.name || '',
            email: seed.email || '',
            phone: seed.phone || '',
            address_legacy: '',
            street: '',
            subdistrict: '',
            district: '',
            city: '',
            province: '',
            postal_code: '',
            latitude: '',
            longitude: '',
            recipient_name: '',
            telephone: '',
            alias: '',
            created_at: dayjs(seed.created_at).format('YYYY-MM-DD HH:mm:ss'),
            updated_at: dayjs(seed.updated_at).format('YYYY-MM-DD HH:mm:ss'),
          };
        }
      }
    }

    await Promise.all(Array.from({ length: concurrency }, () => worker()));
    return out;
  }

  async function exportCSV() {
    try {
      setExporting(true);
      const rows = await hydrateForExport();
      const headers = [
        'id',
        'name',
        'email',
        'phone',
        'address_legacy',
        'street',
        'subdistrict',
        'district',
        'city',
        'province',
        'postal_code',
        'latitude',
        'longitude',
        'recipient_name',
        'telephone',
        'alias',
        'created_at',
        'updated_at',
      ];

      const esc = (v) => {
        if (v == null) return '';
        const s = String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const lines = [headers.join(','), ...rows.map((r) => headers.map((h) => esc(r[h])).join(','))];

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers_export_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  async function exportXLSX() {
    try {
      setExporting(true);
      const rows = await hydrateForExport();
      const XLSX = await ensureXLSX();
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Customers');
      XLSX.writeFile(wb, `customers_export_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    } finally {
      setExporting(false);
    }
  }

  if (loading) return <div>Loading…</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="bagspa-page customers-page">
      <div className="bagspa-page-header customers-page-header">
        <h2 className="mb-0">Customers</h2>
      </div>

      {/* Filter / Action Card */}
      <div className="bagspa-filter-card customers-control-card">
        <div className="customers-control-grid">
          <div className="customers-search-field">
            <label className="form-label">Search</label>
            <input
              className="form-control"
              placeholder="Search name / email / phone..."
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
            />
          </div>

          <div className="customers-rows-field">
            <label className="form-label">Rows</label>
            <select
              className="form-control"
              value={per}
              onChange={(e) => {
                setPage(1);
                setPer(parseInt(e.target.value, 10));
              }}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}/page
                </option>
              ))}
            </select>
          </div>

          <div className="customers-action-group">
            <button
              className="btn btn-outline-primary"
              onClick={exportCSV}
              disabled={exporting || Number(meta.total || 0) === 0}
            >
              {exporting ? 'Preparing…' : 'Export CSV'}
            </button>

            <button
              className="btn btn-primary"
              onClick={exportXLSX}
              disabled={exporting || Number(meta.total || 0) === 0}
            >
              {exporting ? 'Preparing…' : 'Export XLSX'}
            </button>

            <Link to={`${BASE}/customers/new`} className="btn btn-primary">
              + New
            </Link>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="bagspa-table-card customers-table-card">
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle customers-table">
            <thead>
              <tr>
                <th
                  role="button"
                  onClick={() => toggleSort('id')}
                  className="text-nowrap"
                  style={{ width: 80 }}
                >
                  ID {sortBy.key === 'id' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                </th>

                <th role="button" onClick={() => toggleSort('name')}>
                  Name {sortBy.key === 'name' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                </th>

                <th role="button" onClick={() => toggleSort('email')}>
                  Email {sortBy.key === 'email' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                </th>

                <th>Phone</th>

                <th
                  role="button"
                  onClick={() => toggleSort('created_at')}
                  className="text-nowrap"
                >
                  Created At {sortBy.key === 'created_at' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                </th>

                <th style={{ width: 260 }}></th>
              </tr>
            </thead>

            <tbody>
              {paged.map((c) => (
                <tr key={c.id}>
                  <td className="text-monospace">{c.id}</td>
                  <td>{c.name}</td>
                  <td>{c.email}</td>
                  <td>{c.phone}</td>
                  <td>{c.created_at ? new Date(c.created_at).toLocaleDateString('id-ID') : '-'}</td>
                  <td>
                    <div className="btn-group btn-group-sm">
                      <Link className="btn btn-outline-primary" to={`${BASE}/customers/${c.id}`}>
                        Detail
                      </Link>
                      <ManualDeleteButton kind="Customer" label={c.name || `#${c.id}`} onDelete={() => CustomersAPI.destroy(c.id)} onDeleted={load} />
                    </div>
                  </td>
                </tr>
              ))}

              {paged.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-muted">
                    No results.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="text-muted small">{MANUAL_DELETE_NOTE}</p>

        <div className="bagspa-pagination-row customers-pagination-row">
          <div className="text-muted small">
            Total: {meta.total} • Page {pageSafe} / {totalPages}
          </div>

          <div className="btn-group">
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={pageSafe <= 1}
              onClick={() => setPage(1)}
            >
              &laquo;
            </button>

            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={pageSafe <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              &lsaquo;
            </button>

            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={pageSafe >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              &rsaquo;
            </button>

            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={pageSafe >= totalPages}
              onClick={() => setPage(totalPages)}
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
